import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';
import * as moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { COUNTRY_CODE } from '../../../../../../../cmx-api-v7/projects/cmx-api-v7/src/public_api';
import { StorageService } from '../../../../../../../services';

interface FormatDateConfig {
  weekdays: Weekdays;
  months: Weekdays;
  daysInMonth: number[];
  firstDayOfWeek: number;
  rangeSeparator: string;
  weekAbbreviation: string;
  scrollTitle: string;
  toggleTitle: string;
  amPM: string[];
  yearAriaLabel: string;
  monthAriaLabel: string;
  hourAriaLabel: string;
  minuteAriaLabel: string;
  time_24hr: boolean;
}

interface Weekdays {
  shorthand: string[];
  longhand: string[];
}

@Component({
  selector: 'cmx-dashboard-date-picker',
  templateUrl: './cmx-dashboard-date-picker.component.html',
  styleUrls: ['./cmx-dashboard-date-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CmxDashboardDatePickerComponent {
  constructor(private storageService: StorageService) {}

  @Input()
  public placeholder: string;

  private _tempDate = { from: null, to: null };

  public maxDate$ = new BehaviorSubject(null);
  public minDate$ = new BehaviorSubject(null);

  @Input()
  public date = null;

  @Input()
  public labelApply: string;

  @Input()
  public labelCancel: string;

  @Input()
  public country = 'EN';

  @Input()
  public firstDayOfWeek = 0;

  formatDate = (_, __, config: FormatDateConfig) => {
    const { from, to } = this._tempDate;

    const _from = from > to ? to : from;
    const _to = to < from ? from : to;

    moment.defineLocale('tempLocale', {
      parentLocale: 'en',
      months: config.months.longhand
    });

    if (_from && _to) {
      const countryCode = this.storageService.userCountry.trim();

      if (countryCode === COUNTRY_CODE.UNITED_KINGDOM) {
        return `${moment(_from).locale('tempLocale').format('DD MMMM, YYYY')} - ${moment(_to).locale('tempLocale').format('DD MMMM, YYYY')}`;
      }

      if (countryCode === COUNTRY_CODE.USA) {
        return `${moment(_from).locale('tempLocale').format('MMMM DD, YYYY')} - ${moment(_to).locale('tempLocale').format('MMMM DD, YYYY')}`;
      }

      return `${moment(_from).locale('tempLocale').format('DD MMMM, YYYY')} - ${moment(_to).locale('tempLocale').format('DD MMMM, YYYY')}`;
    }

    return null;
  };

  @Output()
  public dateChange = new EventEmitter<{ from: string; to: string }>();

  public updateDateRangeLimit(dateString: string) {
    this._tempDate = {
      ...(this._tempDate.from && this._tempDate.to
        ? {
            from: dateString,
            to: null
          }
        : {
            ...this._tempDate,
            ...(this._tempDate.from ? { to: dateString } : { from: dateString })
          })
    };

    if (this._tempDate.from && !this._tempDate.to) {
      const selectedMonth = new Date(this._tempDate.from).getMonth();

      this.minDate$.next(
        new Date(new Date(this._tempDate.from).setMonth(selectedMonth - 3))
      );
      this.maxDate$.next(
        new Date(new Date(this._tempDate.from).setMonth(selectedMonth + 3))
      );
    } else {
      this.minDate$.next(null);
      this.maxDate$.next(null);
    }
  }

  public resetTmpDateRange() {
    this._tempDate = { from: null, to: null };
  }
}
